import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useToast } from "@/components/ui/use-toast";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";
import { ErrorType } from "@/types";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { TooltipProvider } from "@/components/ui/tooltip";
import { MinimalTiptapEditor } from "@/components/ui/minimal-tiptap";
import { Content } from "@tiptap/react";
import { EmailTrigger } from "@/types/system-setting/system-config";
import { useUpdateEmailTriggerMutation } from "@/services/systemConfigApi";
import { Info } from "lucide-react";
interface PushNotificationFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: string;
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: () => void;
  editModeData: EmailTrigger | undefined;
}
const PushNotificationForm = ({ isOpen, setOpen, action, setAction, editModeData, refetch }: PushNotificationFormProps) => {

  const [pushEnabled, setPushEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);




  const { toast } = useToast();

  // text editor
  const [textEditorValue, setTextEditoralue] = useState<Content>("");

  const [updateEmailTrigger, { isLoading, error, status }] = useUpdateEmailTriggerMutation();

  const initialValues = {
    trigger_name: "",
    is_notification_enabled: false,
    notification_title: "",
    notification_content: "",
    is_email_enabled: false,
    email_subject: "",
    email_body: "",
    is_active: false,
    key: "",
  };

  const form = useForm<EmailTrigger>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  useEffect(() => {
    if (action === "edit" && editModeData) {
      setPushEnabled(editModeData.is_notification_enabled);
      setEmailEnabled(editModeData.is_email_enabled);
      setTextEditoralue(editModeData.email_body || "");
      reset(editModeData);
    } else {
      setPushEnabled(false);
      setEmailEnabled(false);
      setTextEditoralue("");
      reset(initialValues);
    }
  }, [action, editModeData]);

  // Update form when toggles change
  useEffect(() => {
    setValue('is_notification_enabled', pushEnabled);
    setValue('is_email_enabled', emailEnabled);
  }, [pushEnabled, emailEnabled]);

  // Update text editor when form changes
  useEffect(() => {
    setValue('email_body', textEditorValue as string);
  }, [textEditorValue]);

  const onSubmit = async (payload: EmailTrigger) => {
    if (payload.email_body.trim() == "") {
      toast({
        variant: "destructive",
        title: "Email content cannot be empty."
      })
      return;
    }

    try {

      const response = await updateEmailTrigger(payload).unwrap();
      if (response) {
        toast({
          variant: "success",
          title: `Notification ${action == "add" ? "created" : "updated"} successfully`,
        });
        refetch();
        handleClose();
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
        return false;
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
        return false;
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setAction("add");
    reset(
      initialValues,
      {
        keepIsSubmitted: false, // Reset the form's submission state
        keepSubmitCount: false, // Reset the count of submissions
        keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
        keepDefaultValues: true, // Keep default values as provided by initialValue
      }
    );

    setOpen(false);
  };

  console.log({ watcher, errors, action });

  const copyToClipboard = async (text: string) => {
    const isSecure = window.isSecureContext;

    if (isSecure && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        console.log("✅ Used Clipboard API");
        return true;
      } catch (err) {
        console.warn("❌ Clipboard API failed, using fallback", err);
      }
    }

    // fallback
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed"; // avoid scroll jump
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textarea);
      console.log("✅ Used fallback (execCommand)");
      return success;
    } catch (e) {
      console.error("❌ Both Clipboard API and fallback failed", e);
      return false;
    }
  };
  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1100px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form noValidate className="pb-4" onSubmit={handleSubmit(onSubmit, onError)}>
            <SheetHeader className="px-6 sticky top-0 z-40 py-3 bg-white shadow-sm">
              <SheetTitle>
                <div className="flex justify-between flex-col md:flex-row gap-5 items-start bg-white">
                  <div>
                    <p className="font-semibold">System Configuration</p>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">
                        System Configuration
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold ">
                        {action == "add" ? "Create" : "Edit"} Email and Push Notification
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-center space-x-[20px]">
                    <Button
                      type="button"
                      className="w-[100px] text-center flex items-center gap-2 border-primary"
                      variant={"outline"}
                      disabled={isSubmitting}
                      onClick={handleClose}
                    >
                      <i className="fa fa-xmark "></i>
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      className="w-[110px] cursor-pointer bg-primary hover:bg-primary flex items-center gap-1"
                    >
                      {!isSubmitting &&
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      }
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <Separator className=" h-[1px] rounded-full     " />

            <SheetDescription className=" h-full  p-6  ">
              <Card className="w-full ">
                <CardHeader>
                  <CardTitle className="text-base font-medium">
                    Trigger: {watcher.trigger_name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex gap-2 items-start bg-yellow-50 p-4 rounded-sm text-yellow-800">
                      <Info className="h-5 w-5 mt-1" />
                      <div className="space-y-2">
                        <p className="font-medium text-sm">Available placeholders for your message:</p>
                        <div className="flex flex-wrap gap-2">
                          {watcher.replaceable?.split(',').map((placeholder) => (
                            <span
                              key={placeholder}
                              className="bg-yellow-100 px-2 py-1 rounded-md text-xs font-medium hover:bg-yellow-200 transition-colors"
                              title="Click to copy"
                              onClick={async () => {
                                const success = await copyToClipboard(`{{${placeholder}}}`);
                                toast({
                                  variant: success ? "success" : "destructive",
                                  title: success ? "Copied" : "Copy failed",
                                  duration: 2000,
                                });
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {placeholder}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-yellow-700 mt-2">Click on a placeholder to copy to clipboard</p>
                      </div>
                    </div>
                  </div>

                  {/* Push Notification Section */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Label
                        htmlFor="pushNotification"
                        className="text-sm text-zinc-500"
                      >
                        Push Notification
                      </Label>
                      <Switch
                        id="pushNotification"
                        checked={pushEnabled}
                        onCheckedChange={setPushEnabled}
                      />
                    </div>

                    <Separator className=" h-[1px] rounded-full     " />

                    {pushEnabled && (
                      <div className="grid gap-4 grid-cols-3">
                        <div className="space-y-2">
                          <Label htmlFor="notification_title" className="text-sm text-zinc-500">
                            Notification Title
                          </Label>
                          <Input
                            {...register("notification_title", {
                              required: pushEnabled && "Required",
                              maxLength: {
                                value: 50,
                                message: "Maximum 50 characters allowed"
                              },
                              pattern: {
                                value: /^[^@]*$|^.*[^@].*$/,
                                message: "Invalid title"
                              }
                            })}
                            value={watcher.notification_title}
                            placeholder="Enter Title"
                            className="h-10 placeholder:text-zinc-400"
                          />
                          {errors.notification_title?.message && (
                            <span className="text-red-500 text-xs mt-[5px]">
                              {errors.notification_title?.message}
                            </span>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="notification_content" className="text-sm text-zinc-500">
                            Notification Content
                          </Label>
                          <Input
                            {...register("notification_content", {
                              required: pushEnabled && "Required",
                              maxLength: {
                                value: 100,
                                message: "Maximum 100 characters allowed"
                              }
                            })}
                            placeholder="Enter Content"
                            value={watcher.notification_content}
                            className="h-10 placeholder:text-zinc-400"
                          />
                          {errors.notification_content?.message && (
                            <span className="text-red-500 text-xs mt-[5px]">
                              {errors.notification_content?.message}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Email Section */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="email" className="text-sm text-zinc-500">
                        Email
                      </Label>
                      <Switch
                        id="email"
                        checked={emailEnabled}
                        onCheckedChange={setEmailEnabled}
                      />
                    </div>
                    <Separator className=" h-[1px] rounded-full     " />
                    {emailEnabled && (
                      <div className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">

                          <div className="space-y-2">
                            <label htmlFor="email_subject" className="text-sm text-zinc-500">
                              Email Subject
                            </label>
                            <Input
                              type="text"
                              {...register("email_subject", {
                                required: emailEnabled && "Required",
                                maxLength: {
                                  value: 150,
                                  message: "Maximum 150 characters allowed"
                                },
                                validate: (value) => {
                                  const trimmed = value.trim();
                                  const isEmail = /^[\w.-]+@[\w.-]+\.\w{2,}$/i.test(trimmed);
                                  return isEmail ? "Subject must not be an email address" : true;
                                },
                              })}
                              value={watcher.email_subject}
                              placeholder="Enter Subject"
                              className="h-10 placeholder:text-zinc-400"
                            />
                            {errors.email_subject?.message && (
                              <span className="text-red-500 text-xs mt-[5px]">
                                {errors.email_subject?.message}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label
                            htmlFor="emailBody"
                            className="text-sm text-zinc-500"
                          >
                            Email Body
                          </label>
                          <div className="space-y-2">
                            <TooltipProvider>
                              <MinimalTiptapEditor
                                value={textEditorValue}
                                onChange={setTextEditoralue}
                                className="w-full"
                                editorContentClassName="p-5"
                                output="html"
                                placeholder="Type your description here..."
                                autofocus={true}
                                editable={true}
                                editorClassName="focus:outline-none"
                              />
                            </TooltipProvider>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet >
  );
};

export default PushNotificationForm;
